/**
 * Signage player entry. Wires together the SignageApi, DeviceController,
 * RenderingRouter, diagnostic overlay trigger, and Sentry. See
 * signage-web-player-spec.md for the full architecture.
 */

import { SignageApi } from './api.js';
import { DeviceController } from './state/controller.js';
import { DeviceState } from './state/machine.js';
import { renderBoot } from './screens/boot.js';
import { renderUnpaired } from './screens/unpaired.js';
import { RenderingRouter } from './screens/rendering.js';
import { renderUpgrade } from './screens/upgrade.js';
import { initSentry } from './sentry.js';
import { applyTokensToRoot, scaleFactor } from './design/tokens.js';
import { installKeyboardTrigger } from './diagnostics/keyboard.js';
import { openDiagnostic, DiagnosticHandle } from './diagnostics/overlay.js';

const APP_VERSION = '1.0.0';

function readMeta(name: string): string | undefined {
  const el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  return el?.content || undefined;
}

function browserSupported(): boolean {
  const c = typeof globalThis.crypto === 'undefined' ? undefined : globalThis.crypto;
  return (
    typeof fetch === 'function' &&
    typeof AbortController === 'function' &&
    typeof CSS !== 'undefined' &&
    typeof CSS.supports === 'function' &&
    CSS.supports('aspect-ratio: 1/1') &&
    !!c &&
    (typeof c.randomUUID === 'function' || typeof c.getRandomValues === 'function')
  );
}

function renderUnsupported(root: HTMLElement): void {
  root.innerHTML = '';
  const div = document.createElement('div');
  div.className = 'ag-placeholder';
  const title = document.createElement('h1');
  title.className = 'ag-placeholder__title';
  title.textContent = 'Browser not supported';
  const sub = document.createElement('p');
  sub.className = 'ag-placeholder__sub';
  sub.textContent =
    'Please open this display in a recent Chromium-based browser (Chrome 120+, Edge 120+, WebOS 6+, Tizen 2024+).';
  div.appendChild(title);
  div.appendChild(sub);
  root.appendChild(div);
}

function resolveApiBase(): string {
  const attr = document.body.dataset.apiBase;
  if (attr) return attr;
  const host = window.location.host;
  if (host.startsWith('signage.')) {
    return `${window.location.protocol}//api.${host.slice('signage.'.length)}`;
  }
  if (host.includes('localhost') || host.startsWith('127.')) {
    return `${window.location.protocol}//${window.location.hostname}:8010`;
  }
  return window.location.origin;
}

async function main(): Promise<void> {
  const root = document.getElementById('app');
  if (!root) {
    throw new Error('missing #app root element');
  }

  if (!browserSupported()) {
    renderUnsupported(root);
    return;
  }

  // Sentry init is silent-no-op when no DSN is configured.
  initSentry({
    dsn: readMeta('alora:sentry-dsn'),
    appVersion: APP_VERSION,
  });

  // Publish design tokens as CSS custom properties on the app root so
  // stylesheets and inline styles can consume var(--sig-*) uniformly.
  // Spec: signage-design-tokens-spec §11.
  applyTokensToRoot(root);

  // Resolution-aware root font-size. Drives rem-scaled children + makes
  // 4K UHD displays pick up the 1.5× typography band without duplicating
  // per-mode scaling logic. Spec: signage-design-tokens-spec §10.
  const applyRootScale = (): void => {
    const f = scaleFactor(window.innerHeight);
    // 16px × factor — CSS base. Current panels use px literals not rem so
    // this is preparatory for the gradual px→rem migration; meanwhile it
    // also updates our `scaledTypography()` consumers on resize.
    document.documentElement.style.fontSize = `${(16 * f).toFixed(1)}px`;
  };
  applyRootScale();
  window.addEventListener('resize', applyRootScale, { passive: true });

  renderBoot(root);

  const api = new SignageApi({ baseUrl: resolveApiBase() });
  const controller = new DeviceController({ api, appVersion: APP_VERSION });

  const router = new RenderingRouter(root);
  let diagnostic: DiagnosticHandle | null = null;

  const detachKeyboard = installKeyboardTrigger({
    onOpen: () => {
      if (diagnostic) return;
      diagnostic = openDiagnostic(root, controller);
      // The overlay manages its own close; clear our handle when the DOM
      // loses the modal node so a second Ctrl+Shift+D re-opens cleanly.
      const tick = setInterval(() => {
        if (!root.querySelector('.ag-diag')) {
          diagnostic = null;
          clearInterval(tick);
        }
      }, 500);
    },
    isOverlayOpen: () => diagnostic !== null,
  });

  controller.subscribe(() => paint(root, controller, router));

  const teardown = (): void => {
    detachKeyboard();
    controller.stop();
    router.dispose();
    diagnostic?.close();
  };
  window.addEventListener('beforeunload', teardown);
  window.addEventListener('pagehide', teardown);

  try {
    await controller.bootstrap();
  } catch (err) {
    renderBoot(root);
    const banner = document.createElement('div');
    banner.className = 'ag-banner';
    banner.textContent = err instanceof Error ? err.message : 'Unexpected startup error';
    root.appendChild(banner);
  }
}

function paint(root: HTMLElement, controller: DeviceController, router: RenderingRouter): void {
  const state: DeviceState = controller.state;
  switch (state) {
    case 'boot':
      renderBoot(root);
      return;
    case 'unpaired':
    case 'unbound':
      renderUnpaired(root, controller);
      return;
    case 'rendering':
    case 'offline':
    case 'stale':
      router.render(controller);
      return;
    case 'updating':
      renderUpgrade(root, controller);
      return;
  }
}

void main();
